import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  Typography
} from "@material-ui/core";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import React, { useEffect, useState } from "react";

import useStyles from "./styles";

enum AuthMethod {
  SendPhoneCode = "sendPhoneCode",
  SendEmailCode = "sendEmailCode",
  SendQRCode = "sendQRCode"
}

interface CustomCardProps {
  onClick: () => void;
  imageSrc: string;
  countdown?: number;
  authMethod: AuthMethod;
  tag?: {
    label?: string;
    state: "disabled" | "active";
  };
  hasTimer?: boolean;
  loading?: boolean;
  isDisabled?: boolean;
}

const MfaCustomAuthCard: React.FC<CustomCardProps> = ({
  onClick,
  imageSrc,
  countdown = 60,
  authMethod,
  tag,
  hasTimer = false,
  loading = false,
  isDisabled = false
}) => {
  const classes = useStyles({});
  const [timeLeft, setTimeLeft] = useState(countdown);

  const isPhoneCode = authMethod === AuthMethod.SendPhoneCode;
  const isEmailCode = authMethod === AuthMethod.SendEmailCode;
  const isCodeVerification = isPhoneCode || isEmailCode;

  useEffect(() => {
    if (hasTimer && isCodeVerification && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft(prevTime => {
          if (prevTime <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prevTime - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [hasTimer, isCodeVerification]);

  const isTimerDisabled = hasTimer && isCodeVerification && timeLeft > 0;
  const cardDisabled = isDisabled || isTimerDisabled || loading;

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes
      .toString()
      .padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  const shouldShowTag =
    tag?.label &&
    (hasTimer || (tag.label === "Coming Soon" && tag.state === "disabled"));

  return (
    <Card
      className={`${classes.card} ${cardDisabled ? classes.cardDisabled : ""}`}
      style={{
        pointerEvents: cardDisabled ? "none" : "auto",
        opacity: cardDisabled ? 0.5 : 1
      }}
    >
      <CardActionArea
        style={{ display: "flex", height: "100%" }}
        onClick={cardDisabled ? undefined : onClick}
      >
        <CardContent className={classes.content}>
          <Box className={classes.textContainer}>
            <img
              src={imageSrc}
              alt="Card Thumbnail"
              className={classes.image}
            />
            {authMethod === AuthMethod.SendPhoneCode && (
              <Typography variant="body1" className={classes.text}>
                The verification code will be sent to the mobile <br />
                number you registered.
              </Typography>
            )}
            {authMethod === AuthMethod.SendEmailCode && (
              <Typography variant="body1" className={classes.text}>
                The verification code will be sent to your email.
              </Typography>
            )}
            {authMethod === AuthMethod.SendQRCode && (
              <Typography variant="body1" className={classes.text}>
                You will scan a QR code or manually enter the generated code on
                your authenticator app.
              </Typography>
            )}
          </Box>

          {loading ? (
            <CircularProgress className={classes.circularProgress} size={24} />
          ) : (
            <ArrowForwardIosIcon className={classes.arrow} />
          )}
        </CardContent>
      </CardActionArea>

      {shouldShowTag && isTimerDisabled && (
        <Box
          className={`${classes.tag} ${
            cardDisabled || tag.state === "disabled"
              ? classes.tagDisabled
              : classes.tagActive
          }`}
        >
          Use after ({formatTime(timeLeft)})
        </Box>
      )}

      {authMethod === AuthMethod.SendQRCode && (
        <Box className={classes.tagBlue}>Coming Soon</Box>
      )}
    </Card>
  );
};

export { AuthMethod };
export default MfaCustomAuthCard;
